/**
 * Viewer-activity event names emitted from the public shared MEDIA-KIT page and
 * recorded against the share-link access (share-link-ms, FOAM-21606).
 *
 * These strings are a cross-repo contract, not a local detail. roster-pulse-ms
 * matches them literally — `internal/app/shareableactivity/activity_stream.go`
 * maps each onto a manager-facing action and `summary.go` onto the phrase the
 * "see what they did" summary renders, while `internal/domain/sharelink/entity.go`
 * classifies anything ending in `.opened` as a page load rather than a click.
 * foam-web emits the same names for Fluid's own media-kit page, so a viewer
 * moving between the two apps produces one continuous event stream. Renaming any
 * of them silently drops that activity out of the insight.
 */
export const MEDIA_KIT_EVENTS = {
  /**
   * Page load. share-link-ms already writes its own synthetic
   * `share_link_access.created` for the same load and the insight treats both as
   * opens (never as interactions), so emitting this does not inflate clicks.
   */
  opened: 'media_kit.opened',
  /** A connected social platform in the profile header. Carries `platform`. */
  socialClicked: 'media_kit.social_clicked',
  /** A creator-supplied extra link (LinkedIn, personal site, …). Carries `destination`. */
  linkClicked: 'media_kit.link_clicked',
  /** Opening the in-page detail view for one content piece. Carries `content_id`. */
  viewContentDetailsClicked: 'media_kit.view_content_details_clicked',
  /**
   * Following a content tile out to the post on its own platform — a different
   * action from the in-page detail view above, and the only content interaction
   * available at all when a block leaves "Show metrics in shared media kit" off
   * (its default), which is why it is tracked separately rather than folded in.
   *
   * Newer than the other three: roster-pulse-ms has no case for it yet, so it
   * lands as `ActionOther` and renders with a generic phrase until one is added
   * in `activity_stream.go` and `summary.go`. Counted either way — that service
   * maps an unrecognised type rather than dropping it.
   */
  viewContentAtPlatformClicked: 'media_kit.view_content_at_platform_clicked',
} as const;
